import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, useMap } from "react-leaflet";
import L from "leaflet";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { PermissionStatusBadge, ClimateRiskBadge } from "../components/Badges";
import { Map as MapIcon, Filter } from "lucide-react";

function colorForRisk(score) {
  if (score == null) return "#8A857D";
  if (score >= 7) return "#B45B3E";
  if (score >= 4) return "#D08C38";
  return "#4A5D4E";
}

function FitBounds({ points }) {
  const map = useMap();
  React.useEffect(() => {
    if (!points || points.length === 0) return;
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds.pad(0.2), { maxZoom: 8 });
  }, [points, map]);
  return null;
}

export default function MapPage() {
  const [filters, setFilters] = useState({ species: "", to_group: "", riskMin: "" });

  const { data: points = [] } = useQuery({
    queryKey: ["map-points"],
    queryFn: async () => (await api.get("/collections/map")).data,
  });

  const filtered = useMemo(() => {
    return points.filter((p) => {
      if (filters.species && !(p.species_scientific_name || "").toLowerCase().includes(filters.species.toLowerCase()) && !(p.species_common_name || "").toLowerCase().includes(filters.species.toLowerCase())) return false;
      if (filters.to_group && !(p.to_group || "").toLowerCase().includes(filters.to_group.toLowerCase())) return false;
      if (filters.riskMin) {
        const min = Number(filters.riskMin);
        if (!(p.climate_risk_score >= min)) return false;
      }
      return true;
    });
  }, [points, filters]);

  const center = filtered.length ? [filtered[0].lat, filtered[0].lng] : [-25.27, 133.77]; // Australia center default

  return (
    <div className="space-y-6" data-testid="map-page">
      <header>
        <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#8B5A2B]">Map</div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-bold text-[#2C2A28] mt-2">Collection sites</h1>
        <p className="text-[13.5px] text-[#5C5854] mt-2">Every recorded provenance, on Country.</p>
      </header>

      <Card className="p-5 border-[#E6E2D8] shadow-sm bg-white">
        <div className="grid md:grid-cols-4 gap-3 items-end">
          <Filt label="Species" value={filters.species} onChange={(v) => setFilters((f) => ({ ...f, species: v }))} testid="map-filter-species" />
          <Filt label="TO group" value={filters.to_group} onChange={(v) => setFilters((f) => ({ ...f, to_group: v }))} testid="map-filter-to" />
          <Filt label="Min climate risk" value={filters.riskMin} onChange={(v) => setFilters((f) => ({ ...f, riskMin: v }))} type="number" testid="map-filter-risk" />
          <Button variant="outline" onClick={() => setFilters({ species: "", to_group: "", riskMin: "" })} className="border-[#E6E2D8] gap-2" data-testid="map-filter-reset">
            <Filter className="w-3.5 h-3.5" /> Reset
          </Button>
        </div>
      </Card>

      <Card className="border-[#E6E2D8] shadow-sm overflow-hidden">
        <div className="h-[560px] relative">
          <MapContainer center={center} zoom={4} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
            <TileLayer attribution='© OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <FitBounds points={filtered} />
            {filtered.map((p) => (
              <CircleMarker key={p.id} center={[p.lat, p.lng]} radius={9} pathOptions={{
                color: colorForRisk(p.climate_risk_score),
                weight: 2,
                fillColor: colorForRisk(p.climate_risk_score),
                fillOpacity: 0.45,
              }}>
                <Popup>
                  <div className="space-y-1.5 min-w-[180px]">
                    <div className="font-mono-data text-[10px] text-[#8A857D]">{p.collection_id}</div>
                    <div className="font-serif-display italic text-[14px] text-[#2C2A28]">{p.species_scientific_name}</div>
                    <div className="text-[12px] text-[#5C5854]">{p.species_common_name}</div>
                    <div className="text-[11px] text-[#5C5854]">TO: {p.to_group || "—"}</div>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      <PermissionStatusBadge status={p.cultural_permission_status} />
                      <ClimateRiskBadge score={p.climate_risk_score} />
                    </div>
                    <Link to={`/collections/${p.id}`} className="text-[11px] tracking-[0.18em] uppercase font-semibold text-[#8B5A2B] underline-offset-4 hover:underline mt-1 inline-block">
                      Open profile →
                    </Link>
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
          {filtered.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="px-5 py-3 rounded-md bg-white/90 border border-[#E6E2D8] text-[12.5px] text-[#5C5854] flex items-center gap-2">
                <MapIcon className="w-4 h-4 text-[#8B5A2B]" /> No collection sites match your filters.
              </div>
            </div>
          )}
        </div>
      </Card>

      <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-[#5C5854] flex items-center gap-5">
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#4A5D4E]" /> Low risk</span>
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#D08C38]" /> Moderate risk</span>
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#B45B3E]" /> High risk</span>
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#8A857D]" /> Not assessed</span>
      </div>
    </div>
  );
}

function Filt({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#5C5854] mb-2">{label}</div>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="bg-white border-[#E6E2D8]" data-testid={testid} />
    </div>
  );
}
